import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

/**
 * ProfilePage · Nenad Vladović.
 * Section 6.1 Core-Matrix + 7 Seiten-Blueprint. Alias /about → /nenad-vladovic
 * für stabile @id-Referenzen im Entity-Graph.
 *
 * ⚠ Alle sichtbaren Angaben stammen entweder aus dem Entity-Datenvertrag oder
 * sind allgemeine, nicht persönliche Formulierungen. Konkrete Qualifikationen,
 * Zertifikate, Publikationen und Medien-Auftritte werden ERST ergänzt, nachdem
 * Nenad einzelne Belege im Claim-Register freigegeben hat.
 */
export default function NenadProfile() {
  return (
    <div className="min-h-screen bg-[#081328] text-white pt-32 pb-24" data-testid="nenad-profile-page">
      <SEO
        title="Nenad Vladović · Gründer der SAT Akademie"
        description="Nenad Vladović ist der Gründer der SAT Akademie — Schweizer Akademie für Transformation. Mentor, Bewusstseinsforscher und Entwickler des SAT-HTC (Human Transformation Code)."
        path="/nenad-vladovic"
        type="profile"
      />

      {/* Structured Data: ProfilePage · Person · in-line JSON-LD referenziert die
          stabile Person-ID aus dem Global-Graph in index.html */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "url": "https://sat-akademie.com/nenad-vladovic/",
        "inLanguage": "de-CH",
        "mainEntity": { "@id": "https://sat-akademie.com/nenad-vladovic/#person" }
      })}} />

      <div className="container-sat max-w-4xl">
        <div className="text-center mb-16">
          <div className="overline mb-4">Gründer &amp; fachliche Leitung</div>
          <h1 className="serif text-4xl md:text-6xl font-light leading-tight">
            Nenad <span className="italic text-[#D4AF37]">Vladović</span>
          </h1>
          <p className="serif italic text-white/70 text-xl mt-8 max-w-2xl mx-auto leading-relaxed">
            Mentor, Bewusstseinsforscher und Gründer der SAT Akademie.
          </p>
        </div>

        <div className="prose prose-invert max-w-none text-white/80 leading-relaxed space-y-6">
          <p>
            Nenad Vladović verbindet Bewusstseinsarbeit, Human Design, systemische
            Mustererkennung, Körper- und Emotionsarbeit, Wertearbeit, Beziehungsthemen
            und Berufungsentwicklung zu einem eigenständigen Transformationssystem.
          </p>
          <p>
            Mit dem <Link to="/methoden/human-transformation-code" className="text-[#D4AF37] hover:underline">SAT-HTC (Human Transformation Code)</Link> hat er
            einen roten Faden geschaffen, der Menschen dabei unterstützt, alte
            Lebensmuster zu entschlüsseln und ihren Ursprungscode bewusster zu leben.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="nenad-expertise">
            <div className="overline mb-3">Fachgebiete</div>
            <ul className="text-white/75 text-[15px] leading-relaxed space-y-1.5">
              <li>· Coaching &amp; Mentoring</li>
              <li>· Persönliche Transformation</li>
              <li>· Persönlichkeitsentwicklung</li>
              <li>· Bewusstseinsentwicklung</li>
              <li>· Beziehungs-, Bindungs- und Lebenskompetenz</li>
              <li>· Selbstwertregulation &amp; emotionale Selbstverantwortung</li>
              <li>· Ausbildung von Mentoren (SAT MENTOR)</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="nenad-methods">
            <div className="overline mb-3">Entwickelte SAT-Methoden</div>
            <ul className="text-white/75 text-[15px] leading-relaxed space-y-3">
              <li>
                <Link to="/methoden/human-transformation-code" className="text-[#D4AF37] hover:underline">SAT-HTC · Human Transformation Code</Link>
                <div className="text-white/50 text-sm mt-1">12 Codes · 5 Bewegungen · 4 Räume</div>
              </li>
              <li>
                <Link to="/methoden/human-potential-analysis" className="text-[#D4AF37] hover:underline">SAT-HPA · Human Potential Analysis</Link>
                <div className="text-white/50 text-sm mt-1">Reflexionsmodell zur Potenzialklärung</div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-white/60 text-[14px] leading-relaxed">
          <div className="overline mb-3">Redaktioneller Hinweis</div>
          <p>
            Konkrete Qualifikationen, Zertifikate, Medienauftritte und Publikationen
            werden auf dieser Seite ausschliesslich nach dokumentierter, aktueller
            Belegung ausgespielt. Die SAT Akademie grenzt Coaching, Mentoring,
            Selbsterfahrung und spirituelle Reflexion klar von Psychotherapie und
            medizinischer Versorgung ab.
          </p>
        </div>

        <div className="mt-14 text-center">
          <Link to="/erstgespraech" className="btn-primary" data-testid="nenad-cta-erstgespraech">
            Kostenloses Erstgespräch <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
